import { useId, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { QueueSnapshotMetrics, RewindCauseKind, RewindMinute, RewindResponse, StaffingGapQueue } from "@starsach/shared-types";
import { useRewind, useRewindAgents, useStaffingGapMinutes, useStaffingGaps, type RewindParams } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, StatusPill, btnPrimary, btnSecondary, inputClass, label, sentence } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";

type Days = 7 | 30 | 90;

/** The causes in the owner's words (ADR-0016 section 5); the wire value is lower-case. */
export const CAUSE_LABEL: Record<RewindCauseKind, string> = {
  misremembered: "The wait was short",
  closed: "The queue was closed",
  unstaffed: "Nobody was staffed for the queue",
  misrouted: "Misrouted by configuration",
  offered_and_missed: "Offered and missed",
  staffed_but_unavailable: "Staffed but unavailable",
  overloaded: "Overloaded",
  undetermined: "We cannot say",
};

const SOURCE_LABEL: Record<string, string> = { snapshot: "Queue snapshot", contacts: "Contact records", configuration: "Routing configuration", agent_events: "Agent events" };
const VERDICT_TONE = { agrees: "ok", disagrees: "critical", missing: "neutral" } as const;

/**
 * Journeys · Rewind (ADR-0016, requirements 8.8): the staffing-gap
 * dashboard, and the rewind of one queue over a wait - the report at the
 * minute she arrived, the strip across her wait with the records drawn
 * beside it, the configuration at the time, the agents at any minute on
 * request, and the cause at the top. Opened from a contact's queue span
 * on its trace (`?contact=`), from a gap minute, or from the form.
 */
export function RewindSection({ environmentId, canInspect, canSeeAgents }: { environmentId: string; canInspect: boolean; canSeeAgents: boolean }) {
  const [search, setSearch] = useSearchParams();
  const params: RewindParams | null = search.get("contact") || search.get("queue")
    ? { ...(search.get("queue") ? { queue: search.get("queue")! } : {}), ...(search.get("from") ? { from: search.get("from")! } : {}), ...(search.get("to") ? { to: search.get("to")! } : {}), ...(search.get("contact") ? { contact: search.get("contact")! } : {}) }
    : null;
  const open = (p: RewindParams) => setSearch(new URLSearchParams(Object.entries(p).filter(([, v]) => v) as Array<[string, string]>));

  return (
    <div className="grid gap-6">
      {params ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Why did the contact wait: the queue minute by minute, the configuration at the time, and the cause.</p>
            <button type="button" className={btnSecondary} onClick={() => setSearch(new URLSearchParams())}>Back to staffing gaps</button>
          </div>
          <RewindView environmentId={environmentId} params={params} canInspect={canInspect} canSeeAgents={canSeeAgents} />
        </>
      ) : (
        <>
          <StaffingGaps environmentId={environmentId} onRewind={open} />
          <RewindForm environmentId={environmentId} onRewind={open} />
        </>
      )}
    </div>
  );
}

function StaffingGaps({ environmentId, onRewind }: { environmentId: string; onRewind: (p: RewindParams) => void }) {
  const [days, setDays] = useState<Days>(7);
  const [hour, setHour] = useState<{ queue: StaffingGapQueue; hour: number } | null>(null);
  const [cell, setCell] = useState<{ queue: string; date: string; hour: number } | null>(null);
  const q = useStaffingGaps(environmentId, days);
  const fmt = useFormat();
  const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;

  return (
    <Card
      title={
        <span className="flex flex-wrap items-center justify-between gap-3">
          <span>Staffing gaps<Hint id="rewind.staffing-gap" label="staffing gaps" /></span>
          <span role="group" aria-label="Window" className="inline-flex overflow-hidden rounded-lg border border-neutral-300 text-sm font-normal normal-case dark:border-neutral-700">
            {([7, 30, 90] as Days[]).map((d) => (
              <button key={d} type="button" aria-pressed={days === d} onClick={() => { setDays(d); setHour(null); setCell(null); }} className={`min-h-9 px-3 ${days === d ? "bg-accent-soft font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "bg-white text-neutral-700 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-300"}`}>
                {d}d
              </button>
            ))}
          </span>
        </span>
      }
    >
      <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">Minutes with contacts waiting and no agent available, per queue and hour of day (UTC). Pick an hour to see the days, and a day to see the minutes.</p>
      {q.isPending && <LoadingState label="Loading staffing gaps" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && !q.data.available && (
        <EmptyState title="No queue snapshots yet" hint="Staffing gaps are counted from the queue snapshot this environment's stack takes every minute with EnableQueueSnapshots (ADR-0016). Until the first minute lands there is nothing to count." />
      )}
      {q.data && q.data.available && (
        <DataTable
          caption="Staffing gaps per queue"
          rows={q.data.queues}
          rowKey={(r) => r.queueId}
          empty={<EmptyState title="No queues listed" hint="The environment's Connect inventory lists no queues yet." />}
          columns={[
            { key: "queue", header: "Queue", render: (r) => r.name ?? r.queueId },
            { key: "total", header: "Gap minutes", numeric: true, render: (r) => fmt.number(r.total) },
            {
              key: "hours",
              header: "By hour of day (UTC)",
              render: (r) =>
                r.total === 0 ? (
                  <span className="text-neutral-500">None</span>
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {r.byHour.map((n, h) =>
                      n > 0 ? (
                        <button key={h} type="button" aria-pressed={hour?.queue.queueId === r.queueId && hour.hour === h} onClick={() => { setHour({ queue: r, hour: h }); setCell(null); }} className="min-h-7 rounded border border-amber-300 bg-amber-50 px-1.5 text-xs tabular-nums text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                          {hh(h)} · {fmt.number(n)}
                        </button>
                      ) : null,
                    )}
                  </span>
                ),
            },
          ]}
          sortBy={(r) => r.name ?? r.queueId}
        />
      )}
      {hour && (
        <div className="mt-5">
          <h3 className="mb-2 text-sm font-semibold">{hour.queue.name ?? hour.queue.queueId}, {hh(hour.hour)} UTC</h3>
          <DataTable
            caption={`Days with gap minutes at ${hh(hour.hour)}`}
            rows={hour.queue.cells.filter((c) => c.hour === hour.hour)}
            rowKey={(c) => c.date}
            filter={false}
            empty={<EmptyState title="No days" />}
            columns={[
              { key: "date", header: "Day", render: (c) => fmt.day(c.date) },
              { key: "minutes", header: "Gap minutes", numeric: true, render: (c) => fmt.number(c.minutes) },
              { key: "open", header: <span className="sr-only">Open</span>, render: (c) => <button type="button" className={btnSecondary} onClick={() => setCell({ queue: hour.queue.queueId, date: c.date, hour: c.hour })} aria-label={`Show the minutes of ${fmt.day(c.date)} at ${hh(c.hour)}`}>Show minutes</button> },
            ]}
          />
        </div>
      )}
      {cell && <GapMinutes environmentId={environmentId} cell={cell} onRewind={onRewind} />}
    </Card>
  );
}

function GapMinutes({ environmentId, cell, onRewind }: { environmentId: string; cell: { queue: string; date: string; hour: number }; onRewind: (p: RewindParams) => void }) {
  const q = useStaffingGapMinutes(environmentId, cell);
  const fmt = useFormat();
  if (q.isPending) return <LoadingState label="Loading the minutes" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="mt-5">
      <h3 className="mb-2 text-sm font-semibold">Minutes with contacts waiting and no agent available</h3>
      {q.data.missed.length > 0 && <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">{fmt.count(q.data.missed.length, "minute", "minutes")} of this hour had no snapshot<Hint id="rewind.missed" label="missed minutes" />.</p>}
      <DataTable
        caption="Gap minutes"
        rows={q.data.minutes}
        rowKey={(m) => m.minute}
        empty={<EmptyState title="No gap minutes in this hour" />}
        columns={[
          { key: "minute", header: "Minute", render: (m) => fmt.when(m.minute) },
          { key: "waiting", header: "Waiting", numeric: true, render: (m) => fmt.number(m.contactsInQueue) },
          { key: "oldest", header: "Oldest wait", numeric: true, render: (m) => duration(m.oldestContactAgeSeconds) },
          { key: "online", header: "Agents online", numeric: true, render: (m) => fmt.number(m.agentsOnline) },
          { key: "rewind", header: <span className="sr-only">Rewind</span>, render: (m) => <button type="button" className={btnSecondary} onClick={() => onRewind({ queue: q.data.queueId, from: m.minute, to: m.minute })} aria-label={`Rewind ${fmt.when(m.minute)}`}>Rewind</button> },
        ]}
      />
    </div>
  );
}

function RewindForm({ environmentId, onRewind }: { environmentId: string; onRewind: (p: RewindParams) => void }) {
  const gaps = useStaffingGaps(environmentId, 7);
  const fmt = useFormat();
  const ids = { queue: useId(), from: useId(), to: useId(), contact: useId(), error: useId() };
  const [queue, setQueue] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [contact, setContact] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (contact.trim()) return onRewind({ contact: contact.trim() });
    const f = fmt.instantOf(from);
    const t = to ? fmt.instantOf(to) : f;
    if (!queue || !f || !t) return setProblem("Pick a queue and the minute to start from - or give a contact id.");
    setProblem(null);
    onRewind({ queue, from: f.toISOString(), to: t.toISOString() });
  };

  return (
    <Card title="Rewind a queue">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-describedby={problem ? ids.error : undefined}>
        <div>
          <label htmlFor={ids.queue} className={label}>Queue</label>
          <select id={ids.queue} className={inputClass} value={queue} onChange={(e) => setQueue(e.target.value)}>
            <option value="">Choose a queue</option>
            {(gaps.data?.queues ?? []).map((q) => <option key={q.queueId} value={q.queueId}>{q.name ?? q.queueId}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={ids.from} className={label}>From ({fmt.timeZone})</label>
          <input id={ids.from} type="datetime-local" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label htmlFor={ids.to} className={label}>To (optional)</label>
          <input id={ids.to} type="datetime-local" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label htmlFor={ids.contact} className={label}>Or a contact id</label>
          <input id={ids.contact} type="text" className={inputClass} value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Its own queue wait" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-3">
          <button type="submit" className={btnPrimary}>Rewind</button>
          {problem && <p id={ids.error} role="alert" className="text-sm text-red-700 dark:text-red-300">{problem}</p>}
        </div>
      </form>
    </Card>
  );
}

function RewindView({ environmentId, params, canInspect, canSeeAgents }: { environmentId: string; params: RewindParams; canInspect: boolean; canSeeAgents: boolean }) {
  const q = useRewind(environmentId, params);
  const fmt = useFormat();
  const [agentsAt, setAgentsAt] = useState<string | null>(null);
  if (q.isPending) return <LoadingState label="Rewinding" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const r = q.data;
  const first = r.minutes[0];
  const agentsAllowed = r.agentHistory && canInspect && canSeeAgents;

  return (
    <>
      <Card title={<span>Why: {CAUSE_LABEL[r.cause.cause]}<Hint id="rewind.cause" label="the cause" /></span>}>
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">{r.cause.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
        <h3 className="mb-2 text-sm font-semibold">Sources<Hint id="rewind.sources" label="sources" /></h3>
        <DataTable
          caption="What each source says"
          rows={r.cause.sources}
          rowKey={(s) => s.source}
          filter={false}
          empty={null}
          columns={[
            { key: "source", header: "Source", render: (s) => SOURCE_LABEL[s.source] ?? sentence(s.source) },
            { key: "verdict", header: "Verdict", render: (s) => <StatusPill status={s.status} tone={VERDICT_TONE[s.status]} /> },
            { key: "detail", header: "Detail", render: (s) => s.detail },
          ]}
        />
      </Card>

      <Card title={`${r.queue.name ?? r.queue.id} at ${first ? fmt.when(first.minute) : ""}`}>
        {r.contact && <p className="mb-3 text-sm">Contact {r.contact.contactId} entered the queue at {fmt.when(r.contact.enqueuedAt, { seconds: true })}{r.contact.waitSeconds !== undefined ? ` and waited ${duration(r.contact.waitSeconds)}` : ""}{r.contact.disconnectReason ? ` · ended ${sentence(r.contact.disconnectReason.toLowerCase().replace(/_/g, " "))}` : ""}.</p>}
        {first?.snapshot ? <Report m={first.snapshot} /> : <p className="text-sm text-neutral-600 dark:text-neutral-400">No snapshot for this minute<Hint id="rewind.missed" label="missed minute" />.</p>}
      </Card>

      <Card title={`Across the wait · ${fmt.count(r.coverage.observed, "minute observed", "minutes observed")}, ${fmt.count(r.coverage.missed, "missed", "missed")}`}>
        <Strip minutes={r.minutes} />
        <div className="mt-4">
          <DataTable
            caption="The queue minute by minute"
            rows={r.minutes}
            rowKey={(m) => m.minute}
            empty={<EmptyState title="No minutes" />}
            columns={[
              { key: "minute", header: "Minute", render: (m) => fmt.time(m.minute) },
              { key: "waiting", header: "Waiting", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.contacts_in_queue) : <StatusPill status="missed" tone="neutral" />) },
              { key: "records", header: <span>Records<Hint id="rewind.records" label="records" /></span>, numeric: true, render: (m) => fmt.number(m.derived) },
              { key: "oldest", header: "Oldest", numeric: true, render: (m) => (m.snapshot ? duration(m.snapshot.oldest_contact_age_seconds) : "") },
              { key: "online", header: "Online", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_online) : "") },
              { key: "available", header: "Available", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_available) : "") },
              { key: "oncontact", header: "On contact", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_on_contact) : "") },
              { key: "acw", header: "ACW", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_after_contact_work) : "") },
              { key: "np", header: "Non-productive", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_non_productive) : "") },
              { key: "error", header: "Error", numeric: true, render: (m) => (m.snapshot ? fmt.number(m.snapshot.agents_error) : "") },
              { key: "flags", header: "Notes", render: (m) => (m.overflow ? <StatusPill status="overflow into another queue" tone="warning" /> : null) },
              ...(agentsAllowed
                ? [{ key: "agents", header: <span className="sr-only">Agents</span>, render: (m: RewindMinute) => <button type="button" className={btnSecondary} aria-pressed={agentsAt === m.minute} onClick={() => setAgentsAt(m.minute)} aria-label={`Agents at ${fmt.time(m.minute)}`}>Agents</button> }]
                : []),
            ]}
          />
        </div>
        {!r.agentHistory && <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">The agents at a minute are read from this environment's own agent events, which its stack keeps only with EnableAgentEventHistory.</p>}
        {r.agentHistory && !agentsAllowed && <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">The agents at a minute are open to this environment's managers and to Gairm staff.</p>}
      </Card>

      {agentsAt && <Agents environmentId={environmentId} queue={r.queue.id} at={agentsAt} contact={r.contact?.contactId} />}

      <Configuration r={r} />

      <Card title={`Waiting at ${first ? fmt.time(first.minute) : ""}`}>
        <DataTable
          caption="Contacts waiting at the first minute"
          rows={r.waiting}
          rowKey={(c) => c.contactId}
          empty={<EmptyState title="Nobody was waiting by the records" hint="Only ended contacts have records, so a contact still waiting now is not counted." />}
          columns={[
            { key: "contact", header: "Contact", render: (c) => (canInspect ? <Link className="underline" to={`/journeys/${environmentId}/contacts/${c.contactId}`}>{c.contactId}</Link> : c.contactId) },
            { key: "since", header: "In the queue since", render: (c) => fmt.when(c.enqueuedAt, { seconds: true }) },
          ]}
        />
      </Card>
    </>
  );
}

function Report({ m }: { m: QueueSnapshotMetrics }) {
  const fmt = useFormat();
  const cells: Array<[string, string]> = [
    ["Waiting", fmt.number(m.contacts_in_queue)],
    ["Oldest wait", duration(m.oldest_contact_age_seconds)],
    ["Scheduled", fmt.number(m.contacts_scheduled)],
    ["Online", fmt.number(m.agents_online)],
    ["Available", fmt.number(m.agents_available)],
    ["On contact", fmt.number(m.agents_on_contact)],
    ["After-contact work", fmt.number(m.agents_after_contact_work)],
    ["Non-productive", fmt.number(m.agents_non_productive)],
    ["Error", fmt.number(m.agents_error)],
    ["Staffed", fmt.number(m.agents_staffed)],
  ];
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {cells.map(([k, v]) => (
        <div key={k} className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
          <dt className={label}>{k}</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The strip: waiting per minute from the snapshot as bars, the records' count as a mark; a missed minute hatched, overflow underlined. */
function Strip({ minutes }: { minutes: RewindMinute[] }) {
  const w = Math.max(minutes.length * 8, 240);
  const h = 80;
  const max = Math.max(1, ...minutes.map((m) => Math.max(m.snapshot?.contacts_in_queue ?? 0, m.derived)));
  const bw = w / minutes.length;
  const y = (n: number) => h - 12 - (n / max) * (h - 20);
  const missed = minutes.filter((m) => m.missed).length;
  const overflow = minutes.filter((m) => m.overflow).length;
  return (
    <figure>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-24 w-full" role="img" aria-label={`Contacts waiting per minute over ${minutes.length} minutes, at most ${max}; ${missed} minutes missed; ${overflow} minutes marked as overflow.`}>
        {minutes.map((m, i) => (
          <g key={m.minute}>
            {m.snapshot ? (
              <rect x={i * bw + 1} y={y(m.snapshot.contacts_in_queue)} width={Math.max(1, bw - 2)} height={h - 12 - y(m.snapshot.contacts_in_queue)} className="fill-amber-400 dark:fill-amber-600" />
            ) : (
              <rect x={i * bw + 1} y={8} width={Math.max(1, bw - 2)} height={h - 20} className="fill-neutral-200 dark:fill-neutral-800" />
            )}
            <rect x={i * bw + 1} y={y(m.derived) - 1} width={Math.max(1, bw - 2)} height={2} className="fill-sky-700 dark:fill-sky-300" />
            {m.overflow && <rect x={i * bw} y={h - 6} width={bw} height={4} className="fill-red-500" />}
          </g>
        ))}
      </svg>
      <figcaption className="mt-1 flex flex-wrap gap-4 text-xs text-neutral-600 dark:text-neutral-400">
        <span><span aria-hidden className="mr-1 inline-block h-2 w-3 bg-amber-400" />Waiting (snapshot)</span>
        <span><span aria-hidden className="mr-1 inline-block h-0.5 w-3 bg-sky-700" />In the queue (records)</span>
        <span><span aria-hidden className="mr-1 inline-block h-2 w-3 bg-neutral-200" />Missed minute</span>
        <span><span aria-hidden className="mr-1 inline-block h-1 w-3 bg-red-500" />Overflow into another queue<Hint id="rewind.overflow" label="overflow" /></span>
      </figcaption>
    </figure>
  );
}

function Agents({ environmentId, queue, at, contact }: { environmentId: string; queue: string; at: string; contact: string | undefined }) {
  const q = useRewindAgents(environmentId, { queue, at, ...(contact ? { contact } : {}) });
  const fmt = useFormat();
  return (
    <Card title={<span>Agents for the queue at {fmt.time(at)}<Hint id="rewind.agents" label="agents at a minute" /></span>}>
      {q.isPending && <LoadingState label="Reading the agents' events" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && (
        <>
          {q.data.missedBy.length > 0 && (
            <p role="status" className="mb-3 text-sm">Offered to and missed by: {q.data.missedBy.map((m) => `${m.name} (${sentence(m.state.toLowerCase())})`).join(", ")}.</p>
          )}
          <DataTable
            caption="Agents eligible for the queue at the minute"
            rows={q.data.agents}
            rowKey={(a) => `${a.name}#${a.username ?? ""}`}
            empty={<EmptyState title="No agent could take this queue at that minute" hint="Nobody logged in had a routing profile with this queue." />}
            sortBy={(a) => a.name}
            columns={[
              { key: "name", header: "Agent", render: (a) => a.name },
              { key: "status", header: "Status", render: (a) => <StatusPill status={a.status.toLowerCase()} tone={a.statusType === "ROUTABLE" ? "ok" : "neutral"} /> },
              { key: "since", header: "Since", render: (a) => (a.since ? fmt.when(a.since) : "") },
              { key: "profile", header: "Routing profile", render: (a) => a.routingProfile ?? "" },
              { key: "contacts", header: "Contacts", render: (a) => (a.contacts.length ? a.contacts.map((c) => `${sentence(c.state.toLowerCase().replace(/_/g, " "))}${c.channel ? ` (${c.channel})` : ""}`).join(", ") : "None") },
            ]}
          />
          <p className="mt-2 text-xs text-neutral-500">Read from {fmt.count(q.data.read.objects, "file", "files")} of the environment's agent events{q.data.read.truncated ? ", stopped at the read limit" : ""}. Names are shown to you and not stored.</p>
        </>
      )}
    </Card>
  );
}

function Configuration({ r }: { r: RewindResponse }) {
  const fmt = useFormat();
  const c = r.configuration;
  return (
    <Card title={`Configuration at ${fmt.when(c.at)}`}>
      {!c.available ? (
        <EmptyState title="No configuration on file for this minute" hint="The routing configuration is kept from the time the environment's stack started sending it with EnableQueueSnapshots." />
      ) : (
        <>
          <p className="mb-3 text-sm">
            {c.queue ? <>Queue {c.queue.name}{c.queue.maxContacts !== undefined ? `, at most ${fmt.number(c.queue.maxContacts)} waiting` : ""}{c.queue.status ? `, ${sentence(c.queue.status.toLowerCase())}` : ""}. </> : null}
            {c.hours ? <>Hours of operation {c.hours.name} ({c.hours.timezone}): {c.hours.openAtEnqueue ? "open" : "closed"} at the time.</> : "No hours of operation on file."}
          </p>
          <DataTable
            caption="Routing profiles that fed the queue"
            rows={c.profiles}
            rowKey={(p) => `${p.id}#${p.channel}`}
            sortBy={(p) => p.name}
            empty={<EmptyState title="No routing profile fed this queue" hint="No agent could be offered a contact from it." />}
            columns={[
              { key: "name", header: "Routing profile", render: (p) => p.name },
              { key: "channel", header: "Channel", render: (p) => p.channel },
              { key: "priority", header: "Priority", numeric: true, render: (p) => fmt.number(p.priority) },
              { key: "delay", header: "Delay", numeric: true, render: (p) => duration(p.delaySeconds) },
              { key: "concurrency", header: "At once", numeric: true, render: (p) => (p.concurrency !== undefined ? fmt.number(p.concurrency) : "") },
            ]}
          />
        </>
      )}
    </Card>
  );
}

/** A duration in minutes and seconds - a count, not a clock, so it is not a locale's business. */
function duration(s: number): string {
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} m` : `${m} m ${s % 60} s`;
}
